import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { Spin, Collapse, Breadcrumb, Tooltip, Modal } from "antd";
import { toast } from "react-toastify";
import {
    fetchReimbursementById,
    syncReimbursementByRef,
    fetchDynamicConfig,
    downloadReimbursementForm,
    createReimbursementRequest,
} from "../../../core/data/redux/reimbursementSlice";
import { all_routes } from "../../router/all_routes";
import {
    DynamicFieldConfig,
    FieldType,
    FieldOption,
    isBoolean,
    isCheckboxType,
    isDropdownType,
    isDateType,
    resolveDisplayValue,
    resolveLineValue,
    parseMsDate,
    parseToIso,
    isInvalidDefaultValue,
    isEditableUserEntryField,
    isProfileField,
} from "../reimbursement/components/dynamicFieldUtils";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;
const { Panel } = Collapse;

// ── Static helpers and utilities ──────────────────────────────────────────────

const formatDateTime = (iso: string): string => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${dd}-${mm}-${d.getFullYear()}, ${hh}:${min}`;
    } catch { return iso; }
};

const parseSapDate = (raw: string): string | null => {
    const m = String(raw ?? "").match(/\/Date\((\d+)\)\//);
    if (!m) return null;
    const d = new Date(parseInt(m[1], 10));
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const parseSapTime = (raw: string): string | null => {
    const t = String(raw ?? "").padStart(6, "0");
    return t.length >= 4 ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : null;
};

const mapStatusLabel = (code: string): string => {
    const s = (code ?? "").toString().toUpperCase().trim();
    const labels: Record<string, string> = {
        N: "New", T: "To Be Approved", P: "Partially Approved",
        A: "Approved", R: "Rejected", D: "Processing Complete",
        X: "Deleted", B: "To Be Processed", C: "Closed",
        K: "Cancelled", S: "Settled",
    };
    return labels[s] ?? (code || "Draft");
};

const getStatusConfig = (code: string) => {
    const s = (code ?? "").toUpperCase();
    const label = mapStatusLabel(code);
    if (["A", "APPROVED", "S", "SETTLED"].includes(s))
        return { color: "#16A34A", bg: "#f0fdf4", border: "#bbf7d0", icon: "ti ti-circle-check", label };
    if (["R", "REJECTED", "K", "CANCELLED", "X", "DELETED"].includes(s))
        return { color: "#DC2626", bg: "#fef2f2", border: "#fecaca", icon: "ti ti-circle-x", label };
    if (["T", "N", "NEW", "TO BE APPROVED"].includes(s))
        return { color: "#EA580C", bg: "#fff7ed", border: "#fed7aa", icon: "ti ti-clock", label };
    if (["P", "PARTIALLY APPROVED"].includes(s))
        return { color: "#F59E0B", bg: "#fffbeb", border: "#fde68a", icon: "ti ti-clock", label };
    if (["D", "PROCESSING COMPLETE", "C", "CLOSED", "B"].includes(s))
        return { color: "#7C3AED", bg: "#f5f3ff", border: "#ddd6fe", icon: "ti ti-clipboard-check", label };
    return { color: "#6C757D", bg: "#f8f9fa", border: "#dee2e6", icon: "ti ti-info-circle", label };
};

const triggerFileDownload = (fileName: string, base64: string, ext: string) => {
    try {
        const bytes = atob(base64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const mimes: Record<string, string> = {
            pdf: "application/pdf",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
        };
        const blob = new Blob([arr], { type: mimes[ext?.toLowerCase()] ?? "application/octet-stream" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(a.href);
    } catch (e) {
        toast.error("Download failed.");
    }
};

// ── Component ─────────────────────────────────────────────────────────────────

const ReimbursementApprovalDetailPage = () => {
    const { id: referenceNumber } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const workItemId = searchParams.get("workItemId") || (location.state as any)?.workItemId || "";

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const routes = all_routes;

    const { user } = useSelector((state: any) => state.authState);

    // Safest fallback: also check localStorage in case Redux state is wiped on refresh
    let localUser: any = {};
    try {
        const userStr = localStorage.getItem("user");
        if (userStr && userStr !== "undefined") {
            localUser = JSON.parse(userStr);
        }
    } catch (e) { }

    const empId: string =
        localUser?.empId ?? localUser?.usrid ?? localUser?.employee_id ??
        user?.data?.user?.empId ?? user?.empId ?? user?.data?.user?.usrid ?? user?.usrid ?? "";

    // ── State ──────────────────────────────────────────────────────────────
    const [isWaiting, setIsWaiting] = useState(false);
    const [reimbDetails, setReimbDetails] = useState<any>(null);
    const [localApprovedAmount, setLocalApprovedAmount] = useState<string>("");
    const [approverRemarks, setApproverRemarks] = useState<string>("");
    const [editedLineItems, setEditedLineItems] = useState<any[]>([]);
    const [editedDynamicFields, setEditedDynamicFields] = useState<Record<string, any>>({});

    const [validationDialog, setValidationDialog] = useState<{
        visible: boolean;
        loading: boolean;
        success: boolean | null;
        message: string;
        bullets: string[];
    }>({ visible: false, loading: false, success: null, message: '', bullets: [] });

    // Config-derived state
    const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig[]>([]);
    const [multilineHeaders, setMultilineHeaders] = useState<DynamicFieldConfig[]>([]);
    const [termConditions, setTermConditions] = useState<any[]>([]);
    const [configDependants, setConfigDependants] = useState<any[]>([]);

    // ── Fetch config after knowing Reimbursement & Request type ───────────
    const fetchConfig = useCallback(async (remType: string, reqType: string, ltcCode: string) => {
        if (!remType) return;
        try {
            const action = await dispatch(
                fetchDynamicConfig({
                    ReimbursementType: remType,
                    RequestType: reqType,
                    LTCSchemeCode: ltcCode,
                    RequestOperation: "AE",     // AE = Approve/Edit
                    EmpID: empId,
                    ReferenceNumber: referenceNumber,
                })
            );
            if (!fetchDynamicConfig.fulfilled.match(action)) return;

            const payload: any = action.payload;
            const cfg = payload?.configData ?? payload;

            // ── DynamicFields ─────────────────────────────────────────────
            const dfRaw = cfg?.DynamicFields ?? cfg?.parsedDynamicFields;
            let fields: DynamicFieldConfig[] = [];
            if (Array.isArray(dfRaw)) {
                fields = dfRaw;
            } else if (typeof dfRaw === "string") {
                try {
                    const p = JSON.parse(dfRaw);
                    fields = Array.isArray(p) ? p : (p?.results ?? []);
                } catch { fields = []; }
            } else if (Array.isArray(cfg?.parsedDynamicFields)) {
                fields = cfg.parsedDynamicFields;
            }
            setDynamicFields(fields);

            // ── MultilineTableHeaders ─────────────────────────────────────
            const mlRaw = cfg?.MultilineTableHeaders ?? cfg?.parsedMultilineTableHeaders;
            let mlHeaders: DynamicFieldConfig[] = [];
            if (Array.isArray(mlRaw)) {
                mlHeaders = mlRaw;
            } else if (typeof mlRaw === "string") {
                try {
                    const p = JSON.parse(mlRaw);
                    mlHeaders = Array.isArray(p) ? p : (p?.results ?? []);
                } catch { mlHeaders = []; }
            }
            setMultilineHeaders(mlHeaders);

            // ── TermCondition ─────────────────────────────────────────────
            const tcRaw = cfg?.TermCondition ?? cfg?.parsedTermCondition;
            let terms: any[] = [];
            if (Array.isArray(tcRaw)) {
                terms = tcRaw;
            } else if (typeof tcRaw === "string") {
                try { terms = JSON.parse(tcRaw) ?? []; } catch { terms = []; }
            }
            setTermConditions(terms);

            // ── Config Dependants (BirthDate fallback) ────────────────────
            const deps = payload?.toDependants ?? cfg?.toDependants;
            setConfigDependants(Array.isArray(deps) ? deps : []);
        } catch (e) {
            console.error("fetchConfig error:", e);
        }
    }, [dispatch, empId, referenceNumber]);

    // ── Sync empty ToDynamics (and re-fetch after sync like mobile does) ──────
    const syncIfEmpty = useCallback(async (details: any) => {
        const td = details?.ToDynamics;
        const isEmpty = !td || (Array.isArray(td) ? td.length === 0 : Object.keys(td).length === 0);
        if (isEmpty && referenceNumber) {
            try {
                await dispatch(syncReimbursementByRef(referenceNumber));
                // Mobile re-fetches details after a successful sync so the
                // freshly synced ToDependants / ToDynamics are reflected in UI
                const action = await dispatch(fetchReimbursementById(referenceNumber));
                if (fetchReimbursementById.fulfilled.match(action)) {
                    const freshData: any = action.payload;
                    setReimbDetails(freshData);
                    const appAmt = freshData?.ApprovedAmount === "0.00"
                        ? (freshData?.ApprovedNumber ?? "")
                        : (freshData?.ApprovedAmount ?? "");
                    setLocalApprovedAmount(appAmt === "0.00" ? "" : appAmt);
                    if (Array.isArray(freshData?.ToMultiLines)) {
                        setEditedLineItems(freshData.ToMultiLines.map((line: any) => ({ ...line })));
                    } else if (Array.isArray(freshData?.ToMultiLines?.results)) {
                        setEditedLineItems(freshData.ToMultiLines.results.map((line: any) => ({ ...line })));
                    }
                }
            } catch { /* silent – data may still display with DefaultValues */ }
        }
    }, [dispatch, referenceNumber]);

    // ── Primary fetch ─────────────────────────────────────────────────────
    const fetchDetails = useCallback(async () => {
        if (!referenceNumber) return;
        setIsWaiting(true);
        try {
            const action = await dispatch(fetchReimbursementById(referenceNumber));
            if (!fetchReimbursementById.fulfilled.match(action)) {
                toast.error("Failed to load reimbursement details.");
                return;
            }
            const data: any = action.payload;
            setReimbDetails(data);

            // Set initial approved amount
            const appAmt = data?.ApprovedAmount === "0.00"
                ? (data?.ApprovedNumber ?? "")
                : (data?.ApprovedAmount ?? "");
            setLocalApprovedAmount(appAmt === "0.00" ? "" : appAmt);

            // Seed line items if present
            if (Array.isArray(data?.ToMultiLines)) {
                setEditedLineItems(data.ToMultiLines.map((line: any) => ({ ...line })));
            } else if (Array.isArray(data?.ToMultiLines?.results)) {
                setEditedLineItems(data.ToMultiLines.results.map((line: any) => ({ ...line })));
            }

            syncIfEmpty(data);
            fetchConfig(
                data?.Reimbursement_Type_ID ?? data?.ReimbursementType ?? "",
                data?.RequestType ?? "",
                data?.LTCSchemeCode ?? ""
            );
        } catch (e) {
            toast.error("An error occurred.");
        } finally {
            setIsWaiting(false);
        }
    }, [dispatch, referenceNumber, fetchConfig, syncIfEmpty]);

    useEffect(() => { fetchDetails(); }, []);   // eslint-disable-line

    // ── Auto-sum APNUM into localApprovedAmount (matching mobile) ───────────
    useEffect(() => {
        if (!Array.isArray(multilineHeaders) || !Array.isArray(editedLineItems)) return;
        const hasApnum = multilineHeaders.some(
            (col: any) => (col?.LineCustomerField || col?.HeaderCustomerField) === 'APNUM'
        );
        if (!hasApnum) return;
        const total = editedLineItems.reduce((sum, line: any) => {
            const val = parseFloat(line?.APNUM ?? '0');
            return sum + (isNaN(val) ? 0 : val);
        }, 0);
        setLocalApprovedAmount(total.toFixed(2));
    }, [editedLineItems, multilineHeaders]);

    // ── Approval Logic ────────────────────────────────────────────────────
    const approveRejectReimbursement = async (type: "A" | "R" | "V") => {
        if (type === "R" && !approverRemarks.trim()) {
            toast.error("Please enter remarks for rejection");
            return { success: false };
        }

        if (type !== "V") {
            setIsWaiting(true);
        }
        try {
            // Use the component-scope empId which correctly handles user?.data?.user
            const actualUserId = localUser?.usrid ?? localUser?.empId ?? localUser?.employee_id ?? user?.data?.user?.usrid ?? user?.usrid ?? empId;
            const unpaddedUserId = parseInt(actualUserId || "0", 10).toString();

            const userDetails = user?.data?.user?.details ?? user?.details ?? localUser?.details;
            const employeeName = [
                userDetails?.firstname || user?.data?.user?.firstname || user?.firstname,
                userDetails?.lsastname || userDetails?.lastname || user?.data?.user?.lastname || user?.lastname
            ].filter(Boolean).join(" ").trim();

            const remarksList: any[] = Array.isArray(reimbDetails?.remarks_data)
                ? reimbDetails.remarks_data
                : (Array.isArray(reimbDetails?.remarks_data?.results) ? reimbDetails.remarks_data.results : []);

            const transformedRemarksArray = remarksList.map((item: any) => ({
                LineNumber: item.lineNumber ?? item.LineNumber ?? "0000",
                CommentBy: item.commentBy ?? item.CommentBy ?? "E",
                CommentedByUserID: item.commentedByUserId ?? item.commentedByUserID ?? item.CommentedByUserID ?? item.approverId ?? "",
                ApproverLevel: item.approverLevel ?? item.ApproverLevel ?? "00",
                EmployeeName: item.employeeName ?? item.EmployeeName ?? "",
                Comment: item.remarkText ?? item.remark_text ?? item.Comment ?? item.comment ?? "",
                PhaseOfTheRequest1: item.phaseOfTheRequest1 ?? item.PhaseOfTheRequest1 ?? "",
                RequestStatus1: item.requestStatus1 ?? item.RequestStatus1 ?? "",
                PhaseOfTheRequest2: item.phaseOfTheRequest2 ?? item.PhaseOfTheRequest2 ?? "",
                RequestStatus2: item.requestStatus2 ?? item.RequestStatus2 ?? "",
                Checkbox: item.checkbox !== undefined ? item.checkbox : (item.Checkbox !== undefined ? item.Checkbox : true),
                Date: item.createdDate ?? item.Date ?? item.date ?? null,
                Time: item.createdTime ?? item.Time ?? item.time ?? null,
            }));

            const lastRemark = remarksList.length > 0 ? remarksList[remarksList.length - 1] : null;
            const prevPhase = lastRemark?.phaseOfTheRequest2 ?? lastRemark?.PhaseOfTheRequest2 ?? reimbDetails?.PhaseOftheRequest ?? "P";
            const prevStatus = lastRemark?.requestStatus2 ?? lastRemark?.RequestStatus2 ?? reimbDetails?.RequestStatus ?? "T";

            const finalRemarks = [
                ...transformedRemarksArray,
                {
                    LineNumber: "0000",
                    CommentBy: "A",
                    ReferenceNumber: referenceNumber,
                    CommentedByUserID: unpaddedUserId,
                    ApproverLevel: reimbDetails?.ApproverLevel || "01",
                    EmployeeName: employeeName,
                    Comment: approverRemarks || "",
                    PhaseOfTheRequest1: prevPhase,
                    RequestStatus1: prevStatus,
                    PhaseOfTheRequest2: "",
                    RequestStatus2: "",
                    Checkbox: true
                }
            ];

            const dynamicFieldsObject = {
                ...toDynamicsObj,
                ...editedDynamicFields,
                ReferenceNumber: referenceNumber
            };

            const linesPayload = editedLineItems.length > 0
                ? editedLineItems
                : (Array.isArray(reimbDetails?.ToMultiLines)
                    ? reimbDetails.ToMultiLines
                    : (Array.isArray(reimbDetails?.ToMultiLines?.results)
                        ? reimbDetails.ToMultiLines.results
                        : []));

            const payload: any = {
                Action: type,
                EmpID: reimbDetails?.EmpID,
                ReferenceNumber: referenceNumber,
                RequestOperation: "AE",
                RequestedNumber: reimbDetails?.RequestedNumber,
                RequestedAmount: reimbDetails?.RequestedAmount,
                ApprovedAmount: reimbDetails?.ApprovedAmount === "0.00" ? "0.00" : localApprovedAmount,
                ApprovedNumber: reimbDetails?.ApprovedNumber === "0.00" ? "0.00" : localApprovedAmount,
                RequestedTaxExempt: reimbDetails?.RequestedTaxExempt,
                ApprovedTaxExempt: reimbDetails?.ApprovedTaxExempt,
                UnitKey: reimbDetails?.UnitKey,
                Currency: reimbDetails?.Currency,
                ToDependants: reimbDetails?.ToDependants,
                ToDynamics: dynamicFieldsObject,
                ToMultiLines: linesPayload,
                ToRemarks: {
                    results: finalRemarks
                },
                ToAttachments: {
                    results: reimbDetails?.attachments_data ?? []
                },
                ToTransactionDatas: {
                    EmpID: reimbDetails?.EmpID,
                    ReferenceNumber: referenceNumber,
                    ReimbursementType: reimbDetails?.ReimbursementType ?? reimbDetails?.Reimbursement_Type_ID ?? "",
                    RequestType: reimbDetails?.RequestType ?? reimbDetails?.Request_Type ?? "",
                    CreatedSource: reimbDetails?.CreatedSource ?? null,
                    LTCSchemeCode: reimbDetails?.LTCSchemeCode ?? null,
                    LTCBlockBeginDate: reimbDetails?.LTCBlockBeginDate || null,
                    LTCBlockEndDate: reimbDetails?.LTCBlockEndDate || null,
                    ReasonCode: reimbDetails?.ReasonCode ?? null,
                    AppRejDelDate: `/Date(${new Date().getTime()})/`,
                    InfotypeUpdateDate: `/Date(${new Date().getTime()})/`,
                    PaymentDate: !(reimbDetails?.PaymentDate === undefined || reimbDetails?.PaymentDate === "null" || reimbDetails?.PaymentDate === null || reimbDetails?.PaymentDate === "") ? `/Date(${new Date(reimbDetails?.PaymentDate).getTime()})/` : `/Date(${new Date().getTime()})/`,
                    CompletedDate: !(reimbDetails?.CompletedDate === undefined || reimbDetails?.CompletedDate === "null" || reimbDetails?.CompletedDate === null || reimbDetails?.CompletedDate === "") ? `/Date(${new Date(reimbDetails?.CompletedDate).getTime()})/` : `/Date(${new Date().getTime()})/`,
                    InfotypeBeginDate: !(reimbDetails?.InfotypeBeginDate === undefined || reimbDetails?.InfotypeBeginDate === "null" || reimbDetails?.InfotypeBeginDate === null || reimbDetails?.InfotypeBeginDate === "") ? `/Date(${new Date(reimbDetails?.InfotypeBeginDate).getTime()})/` : `/Date(${new Date().getTime()})/`,
                    PhaseOftheRequest: prevPhase,
                    RequestStatus: prevStatus,
                    ApproverLevel: reimbDetails?.ApproverLevel ?? "01",
                    ApproverUserID: unpaddedUserId,
                    ID: workItemId || reimbDetails?.ToTransactionDatas?.ID || reimbDetails?.workItemId || "",
                    ApproverGroup: reimbDetails?.ApproverGroup ?? null,
                    ReferenceNumber1: reimbDetails?.ReferenceNumber1 ?? null,
                    TriggerRendering: reimbDetails?.TriggerRendering ?? null,
                    TriggerRendering1: reimbDetails?.TriggerRendering1 ?? null,
                    OffcycleRequested: reimbDetails?.OffcycleRequested ?? true,
                    TaxExemRequired: reimbDetails?.TaxExemRequired ?? false,
                    OffcycleProvided: reimbDetails?.OffcycleProvided ?? true,
                    DeclAgreement: reimbDetails?.DeclAgreement ?? true,
                    PartialClaim: reimbDetails?.PartialClaim ?? false,
                    TripCompleted: reimbDetails?.TripCompleted ?? false,
                    PartNumber: reimbDetails?.PartNumber ?? null,
                }
            };

            const action = await dispatch(createReimbursementRequest(payload));

            // Extract the logical payload (it might be a fulfilled action with a business error inside)
            const actionPayload: any = action.payload;
            const innerData = actionPayload?.data;
            const deepData = actionPayload?.data?.data;
            const isLogicalError =
                actionPayload?.success === false ||
                innerData?.success === false ||
                deepData?.success === false;

            if (createReimbursementRequest.fulfilled.match(action) && !isLogicalError) {
                if (type === "V") {
                    return { success: true, message: "Validation Successful", bullets: [] };
                }
                toast.success(`Request ${type === "A" ? "Approved" : "Rejected"} successfully`);
                navigate(-1);
                return { success: true };
            } else {
                // Determine the error object source
                const errObj = isLogicalError
                    ? (deepData?.success === false ? deepData : (innerData?.success === false ? innerData : actionPayload))
                    : (typeof actionPayload === "string" ? { message: actionPayload } : (actionPayload || {}));

                const innerErr = errObj?.innerError ||
                    errObj?.error?.innererror?.errordetails ||
                    deepData?.innerError ||
                    innerData?.innerError ||
                    actionPayload?.innerError;
                const bullets = Array.isArray(innerErr) && innerErr.length > 0
                    ? innerErr.map((e: any) => typeof e === 'string' ? e : (e?.message || e?.description || String(e)))
                    : [];

                let extractedMsg = "";
                if (typeof errObj?.message === "string" && errObj.message) {
                    extractedMsg = errObj.message;
                } else if (typeof deepData?.message === "string" && deepData.message) {
                    extractedMsg = deepData.message;
                } else if (typeof innerData?.message === "string" && innerData.message) {
                    extractedMsg = innerData.message;
                } else if (typeof actionPayload?.message === "string" && actionPayload.message) {
                    extractedMsg = actionPayload.message;
                } else if (typeof actionPayload === "string" && actionPayload) {
                    extractedMsg = actionPayload;
                } else if (typeof errObj?.error === "string" && errObj.error) {
                    extractedMsg = errObj.error;
                } else if (typeof errObj?.error?.message === "string" && errObj.error.message) {
                    extractedMsg = errObj.error.message;
                }

                if (!extractedMsg) {
                    extractedMsg = bullets.length > 0 ? "" : "Failed to process request. Please check the network tab.";
                }

                if (type === "V") {
                    return { success: false, message: bullets.length > 0 ? '' : extractedMsg, bullets };
                }

                const toastMsg = bullets.length > 0
                    ? `${extractedMsg ? extractedMsg + "\n" : ""}${bullets.map((b: string) => `• ${b}`).join("\n")}`
                    : extractedMsg;

                toast.error(toastMsg || "Failed to process request.");
                return { success: false };
            }
        } catch (e: any) {
            const errText = typeof e === 'string' ? e : (e?.message || "An error occurred");
            toast.error(errText);
            if (type === "V") return { success: false, message: errText, bullets: [] };
        } finally {
            if (type !== "V") {
                setIsWaiting(false);
            }
        }
    };

    const handleValidationAndApprove = async () => {
        setValidationDialog({ visible: true, loading: true, success: null, message: '', bullets: [] });
        const result: any = await approveRejectReimbursement("V");
        if (result?.success) {
            setValidationDialog({ visible: true, loading: false, success: true, message: "Validation Successful", bullets: [] });
        } else {
            setValidationDialog({ visible: true, loading: false, success: false, message: result?.message || "Validation failed.", bullets: result?.bullets || [] });
        }
    };

    // ── Derived data ──────────────────────────────────────────────────────

    /** ToDynamics flat object (handles both array and object shapes) */
    const toDynamicsObj: Record<string, any> = (() => {
        const td = reimbDetails?.ToDynamics;
        if (!td) return {};
        if (Array.isArray(td)) return td[0] ?? {};
        return td;
    })();

    /**
     * Visible dynamic fields for Approver (mirrors mobile logic):
     * Filtered by FieldVisibleToApp === true and sorted by FieldsOrder + UIPosition
     */
    const approverVisibleFields = dynamicFields
        .filter((f) => isBoolean(f.FieldVisibleToApp))
        .sort((a, b) => {
            const oa = parseInt(a.FieldsOrder ?? "0", 10);
            const ob = parseInt(b.FieldsOrder ?? "0", 10);
            if (oa !== ob) return oa - ob;
            const posOrder: Record<string, number> = { L: 0, C: 1, R: 2 };
            const pa = posOrder[(a.UIPosition ?? "L")[0]] ?? 0;
            const pb = posOrder[(b.UIPosition ?? "L")[0]] ?? 0;
            return pa - pb;
        });

    /** Filter ToMultiLines into a proper array */
    const multiLines: any[] = (() => {
        const ml = reimbDetails?.ToMultiLines;
        if (!ml) return [];
        if (Array.isArray(ml)) return ml;
        if (Array.isArray(ml?.results)) return ml.results;
        return [];
    })();

    /** Reorder From Place (C40T1) and To Place (C40T2) so they are adjacent */
    const reorderFromToPlace = (cols: any[]): any[] => {
        const result = [...cols];
        const fromIndex = result.findIndex(c => (c?.LineCustomerField || c?.HeaderCustomerField) === 'C40T1');
        const toIndex = result.findIndex(c => (c?.LineCustomerField || c?.HeaderCustomerField) === 'C40T2');
        if (fromIndex !== -1 && toIndex !== -1 && toIndex !== fromIndex + 1) {
            const [toCol] = result.splice(toIndex, 1);
            const newFromIndex = result.findIndex(c => (c?.LineCustomerField || c?.HeaderCustomerField) === 'C40T1');
            result.splice(newFromIndex + 1, 0, toCol);
        }
        return result;
    };

    /** MultilineHeaders filtered for visibility (FieldVisibleToApp !== false) */
    const visibleLineHeaders = reorderFromToPlace(
        multilineHeaders.filter((h) => h.FieldVisibleToApp !== false && String(h.FieldVisibleToApp).toLowerCase() !== "false")
    );

    /**
     * ALL dependants from the request (mobile shows all, not just selected).
     * DependentSelected flag is preserved so we can render a checkbox per row.
     */
    const allDependants: any[] = (() => {
        const td = reimbDetails?.ToDependants;
        if (!td) return [];
        if (Array.isArray(td)) return td;
        if (Array.isArray(td?.results)) return td.results;
        return [];
    })();

    // Keep selectedDependants for payload building (only selected ones go in submit)
    const selectedDependants: any[] = allDependants.filter((d: any) => d?.DependentSelected);

    /** Filtered remarks (exclude system/ABAP entries) */
    const filteredRemarks: any[] = (reimbDetails?.remarks_data ?? []).filter(
        (r: any) =>
            r?.commentBy !== "S" &&
            r?.approverId !== "" &&
            r?.approverId !== "" &&
            r?.employeeName !== ""
    );

    /** Terms joined into readable paragraphs */
    const termParagraphs = (() => {
        const paras: string[] = [];
        let cur = "";
        termConditions.forEach((tc: any) => {
            const line = (tc.TextLine ?? "").trim();
            if (line === "") {
                if (cur.trim()) { paras.push(cur.trim()); cur = ""; }
            } else {
                cur += (cur ? " " : "") + line;
            }
        });
        if (cur.trim()) paras.push(cur.trim());
        return paras.filter(Boolean);
    })();

    /** Requested / approved value (use Amount, fallback to Quantity/Number if Amount is 0.00) */
    const requestedValue = reimbDetails?.RequestedAmount === "0.00"
        ? (reimbDetails?.RequestedNumber ?? "-")
        : (reimbDetails?.RequestedAmount ?? "-");
    const approvedValue = reimbDetails?.ApprovedAmount === "0.00"
        ? (reimbDetails?.ApprovedNumber ?? "-")
        : (reimbDetails?.ApprovedAmount ?? "-");

    const isLTC = reimbDetails?.ReimbursementType === "ZLTA" || reimbDetails?.Reimbursement_Type_ID === "ZLTA";
    const statusCfg = getStatusConfig(reimbDetails?.RequestStatus ?? "");

    // ── Guard renders ─────────────────────────────────────────────────────
    if (isWaiting && !reimbDetails) {
        return (
            <div className="page-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <Spin size="large" tip="Loading details…" />
            </div>
        );
    }

    if (!reimbDetails && !isWaiting) {
        return (
            <div className="page-wrapper d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <i className="ti ti-file-off fs-1 text-muted opacity-25 mb-3" />
                <h6 className="text-muted mb-3">Record not found</h6>
                <button className="btn btn-sm btn-outline-secondary px-4 rounded-2" onClick={() => navigate(-1)}>← Back</button>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="page-wrapper" style={{ backgroundColor: "#f4f5f7" }}>
            {/* Overlay spinner for background ops */}
            {isWaiting && reimbDetails && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spin size="large" />
                </div>
            )}

            <div className="content">
                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold text-dark mb-0">Reimbursement Details</h5>
                        <Breadcrumb
                            style={{ fontSize: 11, marginTop: 4 }}
                            items={[
                                { title: <Link to={routes.adminDashboard} className="text-muted">Home</Link> },
                                { title: <Link to={`/${routes.Reimbursement?.list ?? "reimbursement-list"}`} className="text-muted">Reimbursement</Link> },
                                { title: <span style={{ color: "#ab1f24" }}>View Details</span> },
                            ]}
                        />
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm px-3 rounded-2"
                            onClick={() => navigate(-1)}
                        >
                            <i className="ti ti-arrow-left me-1" />Back
                        </button>
                    </div>
                </div>

                {/* ── Status Banner ────────────────────────────────────── */}
                <div className="card border-0 rounded-2 mb-4"
                    style={{ background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, borderLeft: `4px solid ${statusCfg.color}` }}>
                    <div className="card-body py-3 px-4 d-flex align-items-center gap-3">
                        <i className={`${statusCfg.icon}`} style={{ color: statusCfg.color, fontSize: '32px' }} />
                        <div>
                            <div className="text-muted fw-bold mb-0" style={{ fontSize: 10, letterSpacing: 1 }}>REQUEST STATUS</div>
                            <h5 className="fw-bold mb-0" style={{ color: statusCfg.color }}>{statusCfg.label}</h5>
                        </div>
                    </div>
                </div>

                {/* ── Application Info ────────────────────────────────── */}
                <div className="card border shadow-sm rounded-2 mb-4">
                    <div className="card-header bg-light border-bottom py-3 px-4 d-flex align-items-center gap-2">
                        <i className="ti ti-plane text-dark" style={{ fontSize: '22px' }} />
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '18px' }}>Application Info</h5>
                    </div>
                    <div className="card-body px-4 py-4">
                        <div className="row g-4">
                            <InfoRow label="Employee ID" value={reimbDetails?.EmpID} />
                            <InfoRow label="Reference No." value={reimbDetails?.ReferenceNumber} highlight />
                            {reimbDetails?.ReferenceNumber1 && (
                                <InfoRow label="Assoc. Ref. No." value={reimbDetails.ReferenceNumber1} />
                            )}
                            <InfoRow label="Reimbursement Type" value={reimbDetails?.Reimbursement_Type ?? reimbDetails?.ReimbursementType} />
                            <InfoRow label="Request Type" value={reimbDetails?.Request_Type ?? reimbDetails?.RequestType} />
                            {isLTC && (
                                <>
                                    <InfoRow label="Scheme" value={reimbDetails?.LTCSchemeCode === "AI" ? "All India" : "Home Town"} />
                                    <InfoRow label="Block Begin Date" value={parseMsDate(reimbDetails?.LTCBlockBeginDate) || reimbDetails?.LTCBlockBeginDate} />
                                    <InfoRow label="Block End Date" value={parseMsDate(reimbDetails?.LTCBlockEndDate) || reimbDetails?.LTCBlockEndDate} />
                                </>
                            )}
                            <InfoRow label="Requested Value" value={requestedValue} />
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3 px-3 d-flex flex-column gap-1 mb-2">
                                <span className="info-row-lbl">Approved Value</span>
                                <div className="input-group input-group-sm" style={{ maxWidth: "200px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                                    <span className="input-group-text bg-white border-end-0 text-muted" style={{ padding: '0.25rem 0.5rem' }}>₹</span>
                                    <input
                                        type="number"
                                        className="form-control text-end border-start-0 ps-0"
                                        style={{ fontWeight: 600, fontSize: '14px', height: '32px' }}
                                        value={localApprovedAmount}
                                        onChange={(e) => setLocalApprovedAmount(e.target.value)}
                                        placeholder="Enter Amount"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Collapsible sections ──────────────────────────── */}
                <Collapse
                    className="reimb-details-collapse mb-3"
                    bordered={false}
                    expandIconPosition="end"
                    style={{ background: "transparent", display: "flex", flexDirection: "column", gap: 12 }}
                >

                    {/* ── Dynamic Details ────────────────────────────── */}
                    {approverVisibleFields.length > 0 && (
                        <Panel key="details" header={<PanelHdr icon="ti ti-users" title="Details" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <DynamicFieldsApprover
                                fields={approverVisibleFields}
                                toDynamics={toDynamicsObj}
                                editedFields={editedDynamicFields}
                                onFieldChange={(key, value) => {
                                    setEditedDynamicFields((prev) => ({ ...prev, [key]: value }));
                                }}
                            />
                        </Panel>
                    )}

                    {/* ── Line Items ─────────────────────────────────── */}
                    {multiLines.length > 0 && (
                        <Panel key="lines" header={<PanelHdr icon="ti ti-list-details" title="Line Items" />}
                            className="reimb-panel shadow-sm rounded-2">
                            {multiLines.map((row: any, ri: number) => {
                                const isAmt01LockedForAdvance =
                                    toDynamicsObj?.C03T1 === "AD" || editedDynamicFields?.C03T1 === "AD";

                                return (
                                    <div key={ri} className={ri < multiLines.length - 1 ? "pb-3 mb-3 border-bottom" : "pb-1"}>
                                        <div className="fw-bold small mb-2" style={{ color: "#ab1f24" }}>Item {ri + 1}</div>
                                        <div className="row g-0">
                                            {visibleLineHeaders.map((col, ci) => {
                                                const key = col.LineCustomerField ?? col.HeaderCustomerField ?? "";
                                                const rawVal = editedLineItems[ri]?.[key] !== undefined
                                                    ? editedLineItems[ri][key]
                                                    : row[key];
                                                const isColEditable =
                                                    (col.ReadOnlyForApp === false || String(col.ReadOnlyForApp).toLowerCase() === "false") &&
                                                    !(key === "AMT01" && isAmt01LockedForAdvance);

                                                const label = col.Label ?? col.LineCustomerField ?? "";
                                                const labelLower = label.toLowerCase().trim();
                                                const keyLower = key.toLowerCase().trim();

                                                const widthOnESSScreen = parseInt(col.WidthOnESSScreen || "0", 10);
                                                const isAssetField = labelLower.includes("asset") || keyLower.includes("asset");
                                                const isBillNoField = labelLower.includes("bill no") || labelLower.includes("bill number") || keyLower.includes("bill_no") || keyLower.includes("billno") || (labelLower.includes("bill") && !labelLower.includes("amount"));
                                                const isActualBillField =
                                                    labelLower.includes("actual bill") ||
                                                    labelLower.includes("bill amount") ||
                                                    keyLower.includes("act_bill") ||
                                                    keyLower.includes("actualbill");
                                                const isAmountField = isActualBillField || (labelLower.includes("amount") && !labelLower.includes("number") && !labelLower.includes("no") && !labelLower.includes("num"));

                                                const maxLength = widthOnESSScreen > 0 ? widthOnESSScreen : (isAssetField ? 16 : undefined);

                                                if (isColEditable) {
                                                    return (
                                                        <div key={ci} className="col-12 col-sm-6 col-md-4 col-lg-3 px-3 d-flex flex-column gap-1 mb-3">
                                                            <span className="info-row-lbl">{label}</span>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={rawVal ?? ""}
                                                                maxLength={maxLength}
                                                                onChange={(e) => {
                                                                    let val = e.target.value;
                                                                    if (isAssetField) {
                                                                        const max = maxLength || 16;
                                                                        if (val.length > max) {
                                                                            val = val.slice(0, max);
                                                                        }
                                                                    } else if (labelLower.includes("mobile") || labelLower.includes("phone") || labelLower.includes("moblie")) {
                                                                        val = val.replace(/\D/g, "");
                                                                        if (val.length > 10) val = val.slice(0, 10);
                                                                    } else if (labelLower.includes("pan")) {
                                                                        val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                                                                        if (val.length > 10) val = val.slice(0, 10);
                                                                    } else if (isAmountField && !isBillNoField) {
                                                                        if (val !== "" && !/^\d*\.?\d*$/.test(val)) {
                                                                            return;
                                                                        }
                                                                    }
                                                                    if (maxLength && val.length > maxLength) {
                                                                        val = val.slice(0, maxLength);
                                                                    }
                                                                    setEditedLineItems((prev) => {
                                                                        const updated = [...prev];
                                                                        updated[ri] = { ...(updated[ri] || row), [key]: val };
                                                                        return updated;
                                                                    });
                                                                }}
                                                                placeholder={`Enter ${label}`}
                                                            />
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <InfoRow
                                                        key={ci}
                                                        label={label}
                                                        value={resolveLineValue(col, { ...row, ...(editedLineItems[ri] || {}) })}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </Panel>
                    )}

                    {/* ── Dependants ─────────────────────────────────── */}
                    {allDependants.length > 0 && (
                        <Panel key="deps" header={
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <PanelHdr icon="ti ti-users" title={`Dependants (${allDependants.length})`} />
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill px-2 py-0 ms-2"
                                    style={{ fontSize: 11 }}
                                    title="Sync dependants from SAP"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        setIsWaiting(true);
                                        try {
                                            await dispatch(syncReimbursementByRef(referenceNumber!));
                                            const action = await dispatch(fetchReimbursementById(referenceNumber!));
                                            if (fetchReimbursementById.fulfilled.match(action)) {
                                                setReimbDetails(action.payload);
                                            }
                                        } finally {
                                            setIsWaiting(false);
                                        }
                                    }}
                                >
                                    <i className="ti ti-refresh me-1" />Sync
                                </button>
                            </div>
                        }
                            className="reimb-panel shadow-sm rounded-2">
                            {allDependants.map((dep: any, di: number) => {
                                const cfgDep = configDependants.find((c: any) => c?.FamsaObjps === dep?.FamsaObjps);
                                const birthDate = (dep?.BirthDate && dep.BirthDate !== "--")
                                    ? dep.BirthDate
                                    : (cfgDep?.BirthDate ?? "");
                                return (
                                    <div key={di} className={di < allDependants.length - 1 ? "pb-3 mb-3 border-bottom" : "pb-1"}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="fw-bold small" style={{ color: "#ab1f24" }}>
                                                {dep?.FullName ?? dep?.FirstName ?? `Dependant ${di + 1}`}
                                            </span>
                                            {/* Checkbox – mirrors mobile's DependentSelected indicator */}
                                            <i
                                                className={dep?.DependentSelected
                                                    ? "ti ti-square-check-filled text-success"
                                                    : "ti ti-square text-muted opacity-50"}
                                                style={{ fontSize: "26px", lineHeight: 1 }}
                                                title={dep?.DependentSelected ? "Selected" : "Not selected"}
                                            />
                                        </div>
                                        <div className="row g-0">
                                            <InfoRow label="Relation" value={dep?.Name} />
                                            {dep?.Gender && <InfoRow label="Gender" value={dep.Gender === "M" ? "Male" : dep.Gender === "F" ? "Female" : dep.Gender} />}
                                            {birthDate && birthDate !== "--" && <InfoRow label="Birth Date" value={birthDate} />}
                                            {dep?.AgeYears && <InfoRow label="Age" value={`${parseInt(dep.AgeYears, 10)} yrs`} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </Panel>
                    )}

                    {/* ── Previous Remarks ───────────────────────────── */}
                    <Panel key="remarks" header={<PanelHdr icon="ti ti-users" title="Previous Remarks" />}
                        className="reimb-panel shadow-sm rounded-2">
                        {filteredRemarks.length === 0 ? (
                            <EmptyMsg text="No remarks available." />
                        ) : (
                            filteredRemarks.map((r: any, ri: number) => {
                                const dateStr = parseSapDate(r?.createdDate ?? "") ?? (r?.createdAt ? formatDateTime(r.createdAt) : "");
                                const timeStr = parseSapTime(r?.createdTime ?? "");
                                const dt = dateStr && timeStr ? `${dateStr}, ${timeStr}` : dateStr;
                                return (
                                    <RemarkRow
                                        key={ri}
                                        name={r?.employeeName}
                                        userId={r?.approverId}
                                        text={r?.remarkText}
                                        dateTime={dt}
                                        isLast={ri === filteredRemarks.length - 1}
                                    />
                                );
                            })
                        )}
                    </Panel>

                    {/* ── Attachments ────────────────────────────────── */}
                    {(reimbDetails?.attachments_data?.length ?? 0) > 0 && (
                        <Panel key="attach" header={<PanelHdr icon="ti ti-paperclip" title="Attachments" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <div className="d-flex flex-column gap-2">
                                {reimbDetails.attachments_data.map((doc: any, ai: number) => (
                                    <AttachItem key={doc.id ?? ai} doc={doc} />
                                ))}
                            </div>
                        </Panel>
                    )}

                    {/* ── Terms & Conditions ─────────────────────────── */}
                    {termParagraphs.length > 0 && (
                        <Panel key="terms" header={<PanelHdr icon="ti ti-file-description" title="Terms & Conditions" />}
                            className="reimb-panel shadow-sm rounded-2">
                            <ul className="mb-0 ps-3" style={{ lineHeight: 1.7 }}>
                                {termParagraphs.map((p, pi) => (
                                    <li key={pi} className="text-muted small">{p}</li>
                                ))}
                            </ul>
                        </Panel>
                    )}

                </Collapse>

                {/* ── Approver Actions ────────────────────────────── */}
                <div className="card border shadow-sm rounded-2 mb-4">
                    <div className="card-header bg-light border-bottom py-3 px-4 d-flex align-items-center gap-2">
                        <i className="ti ti-users text-dark" style={{ fontSize: '22px' }} />
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '18px' }}>Approver Remarks</h5>
                    </div>
                    <div className="card-body px-4 pt-4 pb-4">
                        <div className="mb-3">
                            <label className="form-label small fw-semibold text-muted">Any Remarks</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Enter your remarks here..."
                                value={approverRemarks}
                                onChange={(e) => setApproverRemarks(e.target.value)}
                            />
                        </div>
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="btn btn-danger px-4 rounded-2"
                                onClick={() => approveRejectReimbursement("R")}
                                disabled={isWaiting}
                            >
                                <i className="ti ti-x me-1" /> Reject
                            </button>
                            <button
                                className="btn btn-success px-4 rounded-2"
                                onClick={handleValidationAndApprove}
                                disabled={isWaiting}
                            >
                                <i className="ti ti-check me-1" /> Approve
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ height: 40 }} />
            </div>

            {/* Validation Dialog Modal */}
            <Modal
                title={validationDialog.loading ? "Validating..." : (validationDialog.success ? "Validation Successful" : "Validation Failed")}
                open={validationDialog.visible}
                onCancel={() => !validationDialog.loading && setValidationDialog(d => ({ ...d, visible: false }))}
                footer={null}
                closable={!validationDialog.loading}
                centered
            >
                <div className="text-center py-3">
                    {validationDialog.loading && (
                        <div className="mb-3">
                            <Spin size="large" />
                        </div>
                    )}
                    {!validationDialog.loading && !!validationDialog.message && (
                        <div className="mb-4">
                            <p className={`fw-semibold mb-2 ${validationDialog.success ? 'text-success' : 'text-danger'}`} style={{ fontSize: 16 }}>
                                {validationDialog.message}
                            </p>
                            {validationDialog.success && (
                                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                    Are you sure you want to submit this approval?
                                </p>
                            )}
                        </div>
                    )}
                    {!validationDialog.loading && validationDialog.bullets.length > 0 && (
                        <div className="text-start p-3 bg-light rounded border mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <ul className="mb-0 text-danger ps-3" style={{ fontSize: 14 }}>
                                {validationDialog.bullets.map((b, i) => (
                                    <li key={i} className="mb-1">{b}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="d-flex justify-content-center gap-2 mt-4">
                        <button
                            className="btn btn-outline-secondary px-4 rounded-2"
                            disabled={validationDialog.loading}
                            onClick={() => setValidationDialog(d => ({ ...d, visible: false }))}
                        >
                            Close
                        </button>
                        {!validationDialog.loading && validationDialog.success && (
                            <button
                                className="btn btn-success px-4 rounded-2"
                                onClick={() => {
                                    setValidationDialog(d => ({ ...d, visible: false }));
                                    approveRejectReimbursement("A");
                                }}
                            >
                                <i className="ti ti-check me-1" /> Approve
                            </button>
                        )}
                    </div>
                </div>
            </Modal>

            {/* ── Styles ──────────────────────────────────────────────── */}
            <style>{`
                .reimb-details-collapse {
                    background: transparent;
                }
                .reimb-details-collapse .ant-collapse-item {
                    background: #fff;
                    border: 1px solid #dee2e6 !important;
                    border-radius: 6px !important;
                    margin-bottom: 12px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .reimb-details-collapse .ant-collapse-header {
                    padding: 12px 20px !important;
                    background: #f8f9fa;
                    font-weight: 600;
                    color: #343a40 !important;
                    align-items: center;
                    border-radius: 6px 6px 0 0 !important;
                }
                .reimb-details-collapse .ant-collapse-content { 
                    border-top: 1px solid #dee2e6; 
                }
                .reimb-details-collapse .ant-collapse-content-box { 
                    padding: 20px !important; 
                }
                .reimb-panel { border: none !important; }
                
                .info-row-lbl { font-size: 13px; color: #6c757d; font-weight: 500; }
                .info-row-val { font-size: 14px; color: #212529; font-weight: 500; }
                .info-row-val.hl { color: #ab1f24; font-weight: 600; }
                
                .remark-line { position: absolute; left: 19px; top: 0; bottom: 0; width: 2px; background: #dee2e6; z-index: 0; }
                .remark-avatar { 
                    width: 40px; height: 40px; border-radius: 6px; background: #6c757d; color: #fff;
                    display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 16px;
                    flex-shrink: 0; position: relative; z-index: 1; 
                }
                
                /* Custom scrollbar for textareas */
                textarea::-webkit-scrollbar { width: 8px; }
                textarea::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 6px; }
                textarea::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 6px; }
                textarea::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}</style>
        </div>
    );
};

// ── Dynamic fields approver renderer (supports editability per field config) ────

const DynamicFieldsApprover: React.FC<{
    fields: DynamicFieldConfig[];
    toDynamics: Record<string, any>;
    editedFields: Record<string, any>;
    onFieldChange: (key: string, value: any) => void;
}> = ({ fields, toDynamics, editedFields, onFieldChange }) => {
    if (!fields.length) return <EmptyMsg text="No details configured for this claim type." />;

    const regular = fields.filter((f) => !isCheckboxType(f.ViewOfFieldType as FieldType));
    const checkboxes = fields.filter((f) => isCheckboxType(f.ViewOfFieldType as FieldType));

    return (
        <>
            {regular.length > 0 && (
                <div className="row g-0">
                    {regular.map((f, i) => {
                        const key = f.HeaderCustomerField;
                        const isEditable = f.ReadOnlyForApp === false || String(f.ReadOnlyForApp).toLowerCase() === "false";
                        const isUserEntry = isEditableUserEntryField(f);
                        const isReadOnlyForEmp = isBoolean(f.ReadOnlyForEmp);

                        let rawVal = editedFields[key];
                        if (rawVal === undefined) {
                            const tdVal = toDynamics[key];
                            if (tdVal !== undefined && tdVal !== null && tdVal !== "") {
                                rawVal = isInvalidDefaultValue(f, tdVal) ? "" : tdVal;
                            } else if (isReadOnlyForEmp && !isUserEntry && !isInvalidDefaultValue(f, f.DefaultValue)) {
                                rawVal = f.DefaultValue ?? "";
                            } else {
                                rawVal = "";
                            }
                        } else if (isInvalidDefaultValue(f, rawVal)) {
                            rawVal = "";
                        }

                        const label = (f.Label || "").endsWith("*") ? (f.Label || "").slice(0, -1).trim() : (f.Label || "");
                        const labelLower = label.toLowerCase().trim();
                        const keyLower = (key || "").toLowerCase().trim();

                        const isMandatory = isBoolean(f.CustFldMandatory);
                        const isDate = isDateType(f.ViewOfFieldType as FieldType, key);
                        const isDropdown = isDropdownType(f.ViewOfFieldType as FieldType);
                        const opts: FieldOption[] = f.OptionsValue?.results ?? [];

                        const widthOnESSScreen = parseInt(f.WidthOnESSScreen || "0", 10);
                        const isAssetField = labelLower.includes("asset") || keyLower.includes("asset");
                        const isBillNoField = labelLower.includes("bill no") || labelLower.includes("bill number") || keyLower.includes("bill_no") || keyLower.includes("billno") || (labelLower.includes("bill") && !labelLower.includes("amount"));
                        const isActualBillField =
                            labelLower.includes("actual bill") ||
                            labelLower.includes("bill amount") ||
                            keyLower.includes("act_bill") ||
                            keyLower.includes("actualbill");
                        const isAmountField = isActualBillField || (labelLower.includes("amount") && !labelLower.includes("number") && !labelLower.includes("no") && !labelLower.includes("num"));

                        const maxLength = widthOnESSScreen > 0 ? widthOnESSScreen : (isAssetField ? 16 : undefined);

                        if (isEditable) {
                            return (
                                <div key={key || i} className="col-12 col-sm-6 col-md-4 col-lg-3 px-3 d-flex flex-column gap-1 mb-3">
                                    <span className="info-row-lbl">
                                        {label}
                                        {isMandatory && <span className="text-danger ms-1">*</span>}
                                    </span>
                                    {isDropdown && opts.length > 0 ? (
                                        <select
                                            className="form-select form-select-sm"
                                            value={rawVal ?? ""}
                                            onChange={(e) => onFieldChange(key, e.target.value)}
                                        >
                                            <option value="">-- Select --</option>
                                            {opts.map((opt, oi) => (
                                                <option key={oi} value={opt.SelectedValue}>
                                                    {opt.SelectedText || opt.SelectedValue}
                                                </option>
                                            ))}
                                        </select>
                                    ) : isDate ? (
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={parseToIso(rawVal)}
                                            onChange={(e) => onFieldChange(key, e.target.value)}
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={rawVal ?? ""}
                                            maxLength={maxLength}
                                            onChange={(e) => {
                                                let val = e.target.value;
                                                if (isAssetField) {
                                                    const max = maxLength || 16;
                                                    if (val.length > max) {
                                                        val = val.slice(0, max);
                                                    }
                                                } else if (labelLower.includes("mobile") || labelLower.includes("phone") || labelLower.includes("moblie")) {
                                                    val = val.replace(/\D/g, "");
                                                    if (val.length > 10) val = val.slice(0, 10);
                                                } else if (labelLower.includes("pan")) {
                                                    val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                                                    if (val.length > 10) val = val.slice(0, 10);
                                                } else if (isAmountField && !isBillNoField) {
                                                    if (val !== "" && !/^\d*\.?\d*$/.test(val)) {
                                                        return;
                                                    }
                                                }
                                                if (maxLength && val.length > maxLength) {
                                                    val = val.slice(0, maxLength);
                                                }
                                                onFieldChange(key, val);
                                            }}
                                            placeholder={`Enter ${label}`}
                                        />
                                    )}
                                </div>
                            );
                        }

                        return (
                            <InfoRow
                                key={key || i}
                                label={label}
                                value={resolveDisplayValue(f, rawVal)}
                                mandatory={isMandatory}
                                readOnly={isBoolean(f.ReadOnlyForEmp)}
                            />
                        );
                    })}
                </div>
            )}

            {checkboxes.length > 0 && (
                <div className="mt-3 pt-3 border-top">
                    <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: 1 }}>
                        Declarations
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {checkboxes.map((f, i) => {
                            const key = f.HeaderCustomerField;
                            const isEditable = f.ReadOnlyForApp === false || String(f.ReadOnlyForApp).toLowerCase() === "false";
                            const rawVal = editedFields[key] !== undefined
                                ? editedFields[key]
                                : (toDynamics[key] !== undefined && toDynamics[key] !== null ? toDynamics[key] : (isBoolean(f.ReadOnlyForEmp) ? f.DefaultValue : ""));
                            const checked = rawVal === true || rawVal === "YES" || String(rawVal ?? "").toLowerCase() === "true" || rawVal === "X";
                            const label = (f.Label || "").endsWith("*") ? (f.Label || "").slice(0, -1).trim() : (f.Label || "");

                            if (isEditable) {
                                return (
                                    <div key={key || i} className="form-check d-flex align-items-center gap-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`check_${key}_${i}`}
                                            checked={checked}
                                            onChange={(e) => onFieldChange(key, e.target.checked)}
                                            style={{ cursor: "pointer", width: 18, height: 18 }}
                                        />
                                        <label className="form-check-label small text-dark fw-medium" htmlFor={`check_${key}_${i}`} style={{ cursor: "pointer" }}>
                                            {label}
                                        </label>
                                    </div>
                                );
                            }

                            return (
                                <div key={key || i} className="d-flex align-items-center gap-2">
                                    <span
                                        className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                            width: 22, height: 22,
                                            background: checked ? "#dcfce7" : "#fef2f2",
                                            border: `1.5px solid ${checked ? "#16a34a" : "#dc2626"}`,
                                        }}
                                    >
                                        <i className={`ti ${checked ? "ti-check" : "ti-x"} fw-bold`}
                                            style={{ fontSize: 11, color: checked ? "#16a34a" : "#dc2626" }} />
                                    </span>
                                    <span className="small text-dark fw-medium">
                                        {label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const InfoRow: React.FC<{
    label: string;
    value?: any;
    highlight?: boolean;
    mandatory?: boolean;
    readOnly?: boolean;
}> = ({ label, value, highlight, mandatory, readOnly }) => (
    <div className="col-12 col-sm-6 col-md-4 col-lg-3 px-3 d-flex flex-column gap-1 mb-3">
        <span className="info-row-lbl">
            {label}
            {mandatory && <span className="text-danger ms-1">*</span>}
            {readOnly && (
                <Tooltip title="Pre-filled by SAP">
                    <i className="ti ti-lock text-muted ms-1" style={{ fontSize: 10 }} />
                </Tooltip>
            )}
        </span>
        <span className={`info-row-val${highlight ? " hl" : ""}`}>{value ?? "-"}</span>
    </div>
);

const PanelHdr: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
    <div className="d-flex align-items-center gap-2">
        <i className={`${icon} text-muted`} style={{ fontSize: '20px' }} />
        <span className="fw-semibold text-dark" style={{ fontSize: '16px' }}>{title}</span>
    </div>
);

const EmptyMsg: React.FC<{ text: string }> = ({ text }) => (
    <div className="text-center py-3 text-muted small">
        <i className="ti ti-info-circle me-1" />{text}
    </div>
);

const RemarkRow: React.FC<{
    name: string; userId: string; text: string; dateTime: string; isLast: boolean;
}> = ({ name, userId, text, dateTime, isLast }) => (
    <div className="d-flex gap-3 mb-3" style={{ position: "relative" }}>
        {!isLast && <div className="remark-line" style={{ top: 38 }} />}
        <div className="remark-avatar">{(name ?? userId ?? "?")[0]?.toUpperCase()}</div>
        <div className="flex-grow-1 rounded-2 p-3" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
            <div className="d-flex justify-content-between flex-wrap gap-1 mb-1">
                <span className="fw-bold text-dark small">
                    {name || "-"}
                    {userId && <span className="text-muted ms-2" style={{ fontSize: 11 }}>({userId})</span>}
                </span>
                {dateTime && (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        <i className="ti ti-clock me-1" />{dateTime}
                    </span>
                )}
            </div>
            <p className="mb-0 text-secondary small" style={{ fontStyle: "italic", lineHeight: 1.5 }}>{text || "—"}</p>
        </div>
    </div>
);

const AttachItem: React.FC<{ doc: any }> = ({ doc }) => {
    const fileName = doc?.FileName ?? doc?.fileName ?? doc?.name ?? "Attachment";
    const fileExt = (doc?.FileExtension ?? doc?.fileExtension ?? (fileName.includes('.') ? fileName.split('.').pop() : "") ?? "").toLowerCase();
    const ext = fileExt.replace(/^\./, "");

    const icon =
        ext === "pdf" ? "ti ti-file-type-pdf text-danger" :
            ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext) ? "ti ti-photo text-primary" :
                ["doc", "docx"].includes(ext) ? "ti ti-file-type-doc text-primary" :
                    ["xls", "xlsx", "csv"].includes(ext) ? "ti ti-file-type-xls text-success" :
                        "ti ti-file text-muted";

    const handleClick = () => {
        const fileUrl = doc?.attachmentUrl || doc?.AttachmentUrl || doc?.attachment_url || doc?.fileUrl || doc?.file_url || doc?.url || doc?.serverURL || doc?.s3Url || doc?.downloadUrl || doc?.signedUrl;
        const content = doc?.Content || doc?.content || doc?.FileContent || doc?.fileContent || doc?.file || "";

        if (fileUrl) {
            window.open(fileUrl, "_blank", "noopener,noreferrer");
        } else if (content) {
            triggerFileDownload(fileName, content, ext || "bin");
        } else {
            toast.warn("No file content available.");
        }
    };

    return (
        <div className="d-flex align-items-center gap-3 p-3 rounded-2 border" role="button" tabIndex={0}
            style={{ background: "#f8f9fa", cursor: "pointer" }}
            onClick={handleClick} onKeyDown={(e) => e.key === "Enter" && handleClick()}>
            <i className={`${icon}`} style={{ fontSize: '24px' }} />
            <div className="flex-grow-1 overflow-hidden">
                <div className="fw-semibold small text-dark text-truncate">{fileName}</div>
                {ext && <div className="text-muted" style={{ fontSize: 11 }}>{ext.toUpperCase()}</div>}
            </div>
            <i className="ti ti-external-link text-muted" style={{ fontSize: '20px' }} />
        </div>
    );
};

export default ReimbursementApprovalDetailPage;
